import type { AxiosRequestConfig } from "axios";
import { http } from "../http-common";
import { PageableResponse } from "../types/pagination";

export type LatestProposalPdfResponse = {
  id?: number;
  proposalId: number;
  proposalNumber: string;
  proposalVersion: string;
  totalPrice?: number | string | null;
  pdfUrl: string;
  createdAt?: string | null;
};

export type ProposalPdfArchiveHistoryItem = LatestProposalPdfResponse;

export type OtherProposalItem = {
  id: number;
  proposalNumber?: string;
  createdAt?: string | null;
  totalPrice?: number | string | null;
  proposalUrl?: string | null;
};

export type ProposalLightItem = {
  id: number;
  proposalNumber?: string | null;
  createdAt?: string | null;
};

// Sayfalı sorgular
const getAll = <T = any>(params: any, config?: AxiosRequestConfig) => {
  return http.get<PageableResponse<T>>("/proposals", { ...config, params });
};

const getMe = <T = any>(params: any, config?: AxiosRequestConfig) => {
  return http.get<PageableResponse<T>>("/proposals/me", { ...config, params });
};

// Sayfalama olmadan sorgular
const getAllLight = (params: any) => {
  return http.get<Array<any>>("/proposals/light", { params });
};

const getAllWithoutPagination = (params: any) => {
  return http.get<Array<any>>("/proposals/all", { params });
};

const getMeWithoutPagination = (params?: any) => {
  return http.get<Array<any>>("/proposals/me/all", { params });
};

const get = (id: Number) => {
  return http.get<any>(`/proposals/${id}`);
};

const getReportDetails = (id: Number) => {
  return http.get<any>(`/proposals/${id}/report-details`);
};

const uploadPdfArchive = (id: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return http.post<LatestProposalPdfResponse>(`/proposals/${id}/pdf-archive`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

const getLatestPdfArchive = (id: number) => {
  return http.get<LatestProposalPdfResponse>(`/proposals/${id}/pdf-archive/latest`);
};

const getLatestPdfArchiveContent = (id: number) => {
  return http.get<Blob>(`/proposals/${id}/pdf-archive/latest/content`, {
    responseType: 'blob',
  });
};

const getPdfArchiveHistory = (id: number) => {
  return http.get<ProposalPdfArchiveHistoryItem[]>(`/proposals/${id}/pdf-archive/history`);
};

const getOtherProposals = (id: number) => {
  return http.get<OtherProposalItem[]>(`/proposals/${id}/other-proposals`);
};

const getPreviousProposals = (id: number) => {
  return http.get<OtherProposalItem[]>(`/proposals/${id}/previous-proposals`);
};

const calculatePrice = (data: any) => {
  return http.post<any>("/proposals/calculate-price", data);
};

const create = (data: any) => {
  return http.post<any>("/proposals", data);
};

const update = (id: any, data: any) => {
  return http.put<any>(`/proposals/${id}`, data);
};

const updateReportDetails = (id: any, data: any) => {
  return http.put<any>(`/proposals/${id}/report-details`, data);
};

const updateRenewalSettings = (
  id: any,
  data: { renewal?: boolean; renewalDate?: string; note?: string }
) => {
  return http.patch<any>(`/proposals/${id}/renewal-settings`, data);
};

const updateAuthorizedPersons = (id: any, data: any) => {
  return http.patch<any>(`/proposals/${id}/authorized-persons`, data);
};

const updateAssignees = (id: any, data: any) => {
  return http.put<any>(`/proposals/${id}/assignees`, data);
};

const updateStatus = (id: any, statusId: any, data?: any) => {
  if (data) {
    return http.put<any>(`/proposals/${id}/status/${statusId}`, data);
  }
  return http.put<any>(`/proposals/${id}/status/${statusId}`);
};

const sendManualEmail = (
  id: any,
  data: { emails: string[]; mailType: 'PLAN' | 'REPORT' | 'FINANCIAL' }
) => {
  return http.post<void>(`/proposals/${id}/manual-email`, data);
};


const updateDocumentId = (id: any, data: any) => {
  return http.put<any>(`/proposals/${id}/document`, data);
};

const cancelContract = (id: any) => {
  return http.put<any>(`/proposals/${id}/contract-cancel`);
};

const updatePrice = (id: any, data: any) => {
  return http.put<any>(`/proposals/${id}/total-price`, data);
};

const remove = (id: any) => {
  return http.delete<any>(`/proposals/${id}`);
};

const getCounts = () => {
  return http.get<Array<any>>("/proposals/counts");
};

const getRenuew = () => {
  return http.get<Array<any>>("/proposals/revenue");
};

const getSummary = () => {
  return http.get<Array<any>>("/proposals/summary");
};

const getRenewSummary = () => {
  return http.get<Array<any>>("/proposals/renew-summary");
};
 
const getDocuments = () => {
  return http.get<Array<any>>("/proposals/count-documents");
};

const getAverage = () => {
  return http.get<Array<any>>("/proposals/average-paid");
};

const monthlyCounts = () => {
  return http.get<Array<any>>("/proposals/monthly-counts");
};

const incomeSharingDistribution = () => {
  return http.get<Array<any>>("/proposals/income-sharing-distribution");
};

const statusSummary = () => {
  return http.get<Array<any>>("/proposals/process-summary");
};

const summaryByDateStatus = (params: any) => {
  return http.get<any>("/proposals/summary-by-date-status", { params });
};

const getFollowUpSummary = (params: any) => {
  return http.get<Array<any>>("/proposals/follow-up-summary", { params });
};

// Ödeme Raporu
const getPaymentReportData = (params: { startInvoiceDate?: string; endInvoiceDate?: string }) => {
  return http.get<any>("/proposals/payment-report-data", { params });
};

const getIncomeShareSummary = (params: any) => {
  return http.get<any>("/proposals/income-share-summary-by-date-status", { params });
};

const ProposalService = {
  getAll,
  getMe,
  getAllLight,
  getAllWithoutPagination,
  getMeWithoutPagination,
  get,
  getReportDetails,
  uploadPdfArchive,
  getLatestPdfArchive,
  getLatestPdfArchiveContent,
  getPdfArchiveHistory,
  getOtherProposals,
  getPreviousProposals,
  create,
  update,
  updateReportDetails,
  updateRenewalSettings,
  updateAuthorizedPersons,
  updateAssignees,
  updateStatus,
  sendManualEmail,
  remove,
  updatePrice,
  calculatePrice,
  updateDocumentId,
  cancelContract,
  getCounts,
  getRenuew,
  getDocuments,
  getSummary,
  getRenewSummary,
  getAverage,
  monthlyCounts,
  incomeSharingDistribution,
  statusSummary,
  summaryByDateStatus,
  getFollowUpSummary,
  getPaymentReportData,
  getIncomeShareSummary,
};

export default ProposalService;
